import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Gift, ShieldCheck } from "lucide-react";
import GoogleButton from "@/components/GoogleButton";
import MagicLink from "@/components/MagicLink";
import { vibrate } from "@/lib/api";

const SLIDES = [
  {
    img: "https://static.prod-images.emergentagent.com/jobs/93f4e5e3-3b06-4947-bbf2-382c6a976f5a/images/6c18e91df8eb21836ef7d0bb826cf636bddc27eb491ce8d9eb767e6a721566d2.png",
    title: "Colle ta conversation",
    desc: "Tinder, Insta, WhatsApp — copie-colle tes messages, le Doc s'occupe du reste.",
  },
  {
    img: "https://static.prod-images.emergentagent.com/jobs/93f4e5e3-3b06-4947-bbf2-382c6a976f5a/images/3b93de7453f6c08bf66914c3eb5961fced4b9d98abdb9fbff7aa7063ed56cef2.png",
    title: "L'IA décrypte tout",
    desc: "Red flags, niveau de game, intentions cachées… décodés en 5 secondes.",
  },
  {
    img: "https://static.prod-images.emergentagent.com/jobs/93f4e5e3-3b06-4947-bbf2-382c6a976f5a/images/8a42c90f73a14afc6c202ffdb454c8066fd7ffd188f9e966b74bf8718a83c7bc.png",
    title: "Réponds comme un boss",
    desc: "3 réponses sur-mesure prêtes à envoyer. Le wow moment, c'est maintenant.",
  },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const isCreator = new URLSearchParams(window.location.search).get("creator") === "1";
  const [step, setStep] = useState(isCreator ? SLIDES.length - 1 : 0);
  const last = step === SLIDES.length - 1;

  useEffect(() => {
    if (!isCreator) return;
    // Creator entry point (home band / prospection email) → route to /createurs after auth.
    localStorage.setItem("dmd_creator_intent", "1");
    const params = new URLSearchParams(window.location.search);
    const cp = params.get("cp");
    if (cp) localStorage.setItem("dmd_cp", cp.toUpperCase());
    const com = params.get("com");
    if (com) localStorage.setItem("dmd_com", com.toUpperCase());
  }, [isCreator]);

  const next = () => {
    vibrate();
    if (last) return;
    setStep((s) => s + 1);
  };

  const slide = SLIDES[step];

  return (
    <div className="grain min-h-[100dvh] bg-[#09090b] text-zinc-100 flex flex-col overflow-hidden">
      <div className="mx-auto max-w-md w-full px-6 pt-8 pb-6 flex flex-col flex-1">
        {!isCreator && (
        <div className="flex items-center justify-between mb-4 shrink-0">
          <div className="flex gap-2">
            {SLIDES.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === step ? "w-8 bg-pink-500" : "w-3 bg-zinc-700"
                }`}
              />
            ))}
          </div>
          <button
            data-testid="onboarding-skip"
            onClick={() => setStep(SLIDES.length - 1)}
            className="text-xs text-zinc-500 hover:text-zinc-300"
          >
            Passer
          </button>
        </div>
        )}

        <div className="flex-1 flex flex-col justify-center min-h-0">
          {isCreator ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="flex flex-col items-center text-center"
              data-testid="onboarding-creator-hero"
            >
              <div className="text-6xl">🤝💸</div>
              <h2 className="font-display text-3xl font-black tracking-tight mt-6">
                Rejoins le <span className="text-pink-400">Programme Créateurs</span>
              </h2>
              <p className="text-zinc-400 mt-3 text-base leading-relaxed max-w-[320px]">
                Connecte-toi pour activer ton lien d'affiliation et gagner <b className="text-white">50% le 1ᵉʳ mois</b> puis <b className="text-white">25% à vie</b> sur chaque abonné·e.
              </p>
            </motion.div>
          ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.35 }}
              className="flex flex-col items-center text-center"
            >
              <div className="relative w-full max-w-[320px] aspect-square rounded-[2rem] overflow-hidden border border-pink-500/20 shadow-[0_0_60px_-15px_rgba(255,0,127,0.5)]">
                <img
                  src={slide.img}
                  alt={slide.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-transparent" />
              </div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="font-display text-3xl font-black tracking-tight mt-8"
              >
                {slide.title}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="text-zinc-400 mt-3 text-base leading-relaxed max-w-[300px]"
              >
                {slide.desc}
              </motion.p>
            </motion.div>
          </AnimatePresence>
          )}
        </div>

        <div className="mt-6 shrink-0">
          {last ? (
            <div className="space-y-3">
              <div
                data-testid="free-tier-badge"
                className="flex items-center justify-center gap-2 rounded-full bg-pink-500/10 border border-pink-500/25 px-4 py-2.5 text-sm font-semibold text-pink-300"
              >
                {isCreator ? (
                  <><Gift size={16} /> Auto-approuvé · zéro plafond</>
                ) : (
                  <><Gift size={16} /> 3 analyses offertes — sans carte bancaire</>
                )}
              </div>
              <GoogleButton testId="onboarding-google-button" label="Continuer avec Google" />
              <div className="flex items-center gap-3 py-0.5">
                <div className="flex-1 h-px bg-zinc-800" />
                <span className="text-[11px] text-zinc-600 uppercase tracking-wider">ou</span>
                <div className="flex-1 h-px bg-zinc-800" />
              </div>
              <MagicLink />
              <p className="flex items-center justify-center gap-1.5 text-center text-xs text-zinc-500">
                <ShieldCheck size={13} className="text-emerald-400" />
                {isCreator ? "On t'emmène direct sur ton espace créateur." : "On analyse ta première conv juste après."}
              </p>
            </div>
          ) : (
            <button
              data-testid="onboarding-next"
              onClick={next}
              className="w-full flex items-center justify-center gap-2 rounded-full bg-pink-500 text-white font-bold py-4 active:scale-95 transition-transform hover:bg-pink-600 glow-pink"
            >
              Suivant <ArrowRight size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
